/**
 * Runtime library cache — shared between `useElementOptions` (loads
 * library rows for dropdown / multiselect option lists) and the
 * form-rule expression evaluator's `lib()` function (0.37.25).
 *
 * Both callers reach into the SAME module-level Map so a library
 * fetched to populate a dropdown is instantly available for a
 * lib() lookup in the same form (and vice versa). Keyed by
 * `<libraryKey>|<lang>` so the same library fetched in two
 * languages doesn't overwrite the earlier result.
 *
 * The cache holds one of three things per key:
 *   - undefined     — never fetched.
 *   - Promise       — fetch in flight; awaiters share the promise.
 *   - { rows, pk }  — fetch complete; usable synchronously.
 *
 * Sync consumers (lookupLibraryValue) treat the last case as "hit"
 * and the first two as "miss" (return undefined, subscribe to the
 * cache-change event, re-evaluate when the fetch completes).
 *
 * Extracted from useElementOptions.js in 0.37.25; that hook still
 * owns the React-facing subscription glue, this module owns the
 * cache itself.
 */
import { formLibraryRowsApi } from '../api/admin';
// 0.37.100 — Register a preload callback with pageCache: as soon
// as a form structure resolves, walk it for =lib() references
// and fire library batches. Overlaps library-network with React
// mount+render instead of waiting for prewarm useEffect.
import { setOnStructureResolved } from './pageCache';
import { localizeRow, localizeRows } from './visitorDates';
import { registerMonthNames, setMonthNamesLoader, getFormatVersion, subscribeFormatChange } from '../utils/format';

// 0.44.69 - The built-in library Months and days supplies the month names
// of the MMM / MMMM date formats: registered whenever it loads, and loaded
// when a format first needs a language's names.
function _registerMonths(lang, rows) {
  const months = (rows || []).filter(r => r && r.kind === 'month')
    .sort((a, b) => Number(a.number) - Number(b.number));
  if (months.length !== 12) return;
  // 0.44.71 - And the month as written in a date (kind month_in_date).
  const inDate = (rows || []).filter(r => r && r.kind === 'month_in_date')
    .sort((a, b) => Number(a.number) - Number(b.number));
  const ok = inDate.length === 12;
  registerMonthNames(lang || 'en', months.map(r => String(r.name || '')), months.map(r => String(r.short || r.name || '')),
    ok ? inDate.map(r => String(r.name || '')) : null, ok ? inDate.map(r => String(r.short || '')) : null);
}
setMonthNamesLoader((lang) => { fetchLibrary('months_and_days', lang).catch(() => {}); });

// Cache: key is `<libraryKey>|<lang>` (see cacheKey below).
const _cache = new Map();
// Subscribers: called with the changed cacheKey whenever a fetch
// resolves (success or failure). Consumers use this to re-render
// on new library data.
const _listeners = new Set();

/* ============================================================
 * 0.37.92 — Debug logging (opt-in).
 *
 * Turn on by appending `?libdebug=1` to the URL, OR by setting
 * window.__DEBUG_LIBS = true before navigation. Every meaningful
 * cache event logs a line with a millisecond timestamp
 * (performance.now() rounded) so admin can copy-paste the console
 * transcript back to me and I can figure out exactly where the
 * delay lives.
 *
 * Events logged:
 *   [libcache] hydrated <n> entries from localStorage
 *   [libcache] lookupLibraryValue HIT   <libKey>|<lang> row-count=<n>
 *   [libcache] lookupLibraryValue MISS  <libKey>|<lang>
 *   [libcache] fetchLibrary START       <libKey>|<lang> (cache=<state>)
 *   [libcache] fetchLibrary RESOLVED    <libKey>|<lang> rows=<n> took=<ms>ms
 *   [libcache] listener notified        <libKey>|<lang>
 *
 * Zero cost when the flag is off (single boolean check, log
 * function short-circuits).
 * ============================================================ */
let _DEBUG = false;
try {
  if (typeof window !== 'undefined') {
    const q = new URLSearchParams(window.location.search || '');
    if (q.get('libdebug') === '1' || window.__DEBUG_LIBS === true) {
      _DEBUG = true;
    }
  }
} catch (_e) { /* SSR or malformed URL — silent */ }
function _log(msg, ...args) {
  if (!_DEBUG) return;
  const t = typeof performance !== 'undefined' && performance.now
    ? Math.round(performance.now())
    : Date.now();
  // eslint-disable-next-line no-console
  console.log(`[libcache t=${t}ms] ${msg}`, ...args);
}

/* ============================================================
 * 0.37.91 — localStorage persistence + stale-while-revalidate.
 *
 * Problem this solves: library-backed labels (0.37.89) hit a
 * cold cache on every fresh page load, so admin sees the value
 * pop in ~1 second later. Prewarming (0.37.90) starts the
 * network fetch at mount but doesn't eliminate the visible
 * gap — the fetch itself takes time.
 *
 * Fix: read cached library payloads from localStorage at module
 * init. Data is available synchronously before the first render,
 * so labels resolve on first paint. In parallel, fetchLibrary
 * ALSO fires a fresh network request on first-call-per-session
 * (see _refreshedThisSession); if the fresh payload differs
 * from the stale one, listeners are notified and the UI
 * re-renders with fresh data. Admin edits to a library
 * propagate on the next page load.
 * ============================================================ */

// 0.44.72 - v2: rows are stored as sent (dates ISO), formatted on read.
const STORAGE_PREFIX = 'gt18:lib:v2:';
// 0.37.93 — Separate prefix for per-row cache entries. Distinguishes
// them from full-library entries in localStorage inspection and
// lets us skip them during full-library hydrate.
const ROW_STORAGE_PREFIX = 'gt18:librow:v2:';

// Track which cacheKeys we've already background-refreshed this
// session. First fetchLibrary(key, lang) always hits the network
// (so admin sees fresh data); subsequent calls in the same
// session reuse the in-memory cache without another round-trip.
const _refreshedThisSession = new Set();
// 0.44.57 - Collections ('collection:<key>') change daily, unlike
// libraries: refetched when the last fetch is over a minute old, so a
// visitor moving between pages sees newly published items.
const COLLECTION_TTL_MS = 60 * 1000;
const _refreshedAt = new Map();
function _expireCollection(key, ck) {
  if (!String(key || '').startsWith('collection:')) return;
  const at = _refreshedAt.get(ck);
  if (at && Date.now() - at > COLLECTION_TTL_MS) _refreshedThisSession.delete(ck);
}
// 0.37.95 — Separate inflight map (parallel to _rowInflight) so
// concurrent stale-revalidate callers reuse one network request
// instead of firing N. Was: stored promises inside _cache but
// only when the slot was empty, which failed for stale entries.
const _libraryInflight = new Map();

// 0.37.93 — Per-row cache tier. Keyed by
// `<libKey>|<keyColumn>|<keyValue>|<lang>`, value is the row dict
// (or null for a known-404). Used by lookupLibraryValue when the
// full library isn't cached — much faster than fetching the whole
// library (~50ms per row vs ~3s for a 10k-row library).
//
// 0.37.95 — Split into two maps to fix concurrent-dedup bug:
//   _rowCache        — resolved values only (rows or null)
//   _rowInflight     — in-flight promises
// Previously stored promises inside _rowCache but only when the
// slot was undefined — so when a stale value was present, N
// concurrent callers each fired their own network request.
const _rowCache = new Map();
const _rowInflight = new Map();
const _rowRefreshedThisSession = new Set();

function _rowCacheKey(libKey, keyColumn, keyValue, lang) {
  return `${libKey}|${keyColumn}|${keyValue}|${lang || ''}`;
}

/* 0.44.72 - Dates are formatted when READ, not when fetched.
 *
 * 0.44.67 formatted date columns once, as the rows arrived, and kept
 * (and stored in the browser) the formatted text. A row fetched before
 * the month names had loaded kept English names, and one stored before
 * the site's format changed kept the old format, until the browser's
 * copy was replaced. Rows are now kept as sent (ISO dates) with their
 * column_types, and formatted on every read with the current format,
 * page language and month names; a change to any of these re-renders
 * the pages that read them. */
const _views = new WeakMap();
function _view(payload) {
  if (!payload || payload instanceof Promise) return payload;
  const types = payload.column_types;
  if (!types || !Object.keys(types).length) return payload;
  const v = `${getFormatVersion()}|${_pageLangNow()}`;
  const memo = _views.get(payload);
  if (memo && memo.v === v) return memo.out;
  const out = { ...payload, rows: localizeRows(payload.rows || [], types) };
  _views.set(payload, { v, out });
  return out;
}
function _rowView(row) {
  if (!row || typeof row !== 'object' || !row.__types) return row;
  const v = `${getFormatVersion()}|${_pageLangNow()}`;
  const memo = _views.get(row);
  if (memo && memo.v === v) return memo.out;
  const { __types, ...plain } = row;
  const out = localizeRow(plain, __types);
  _views.set(row, { v, out });
  return out;
}
function _withTypes(row, types) {
  if (!row || !types || !Object.keys(types).length) return row;
  return { ...row, __types: types };
}
function _pageLangNow() {
  try { return document.documentElement.getAttribute('lang') || ''; } catch { return ''; }
}

/** Try to read a payload from localStorage. Returns null on
 *  miss, on JSON parse error, or if localStorage isn't
 *  available (Safari private mode, SSR, etc.). */
function _loadFromStorage(ck) {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_PREFIX + ck);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Sanity-check the payload shape before trusting it.
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.rows)) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

/** Persist a payload to localStorage. Best-effort — quota
 *  errors, private-mode restrictions, and missing localStorage
 *  are all silently swallowed.
 *  0.37.93 — When _DEBUG is on, log the specific reason so admin
 *  can distinguish quota-exceeded from private-mode-blocked
 *  from serialization-failed. */
function _saveToStorage(ck, payload) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_PREFIX + ck, JSON.stringify(payload));
  } catch (e) {
    _log(`_saveToStorage FAILED ${ck}: ${e && e.name}: ${e && e.message}`);
  }
}

/** At module load, hydrate _cache from any previously-saved
 *  entries in localStorage. Runs once. Safe to call in
 *  environments without localStorage (returns silently).
 *  0.37.93 — Also hydrates the per-row cache tier. */
function _hydrateFromStorage() {
  let libCount = 0;
  let rowCount = 0;
  try {
    if (typeof localStorage === 'undefined') { _log('hydrate skipped — no localStorage'); return; }
    for (let i = 0; i < localStorage.length; i++) {
      const storageKey = localStorage.key(i);
      if (!storageKey) continue;
      // Full-library entry?
      if (storageKey.startsWith(STORAGE_PREFIX)) {
        const ck = storageKey.slice(STORAGE_PREFIX.length);
        const payload = _loadFromStorage(ck);
        if (payload) {
          _cache.set(ck, payload);
          libCount++;
          _log(`hydrated library ${ck} rows=${payload.rows.length}`);
        }
      }
      // Per-row entry?
      else if (storageKey.startsWith(ROW_STORAGE_PREFIX)) {
        const rck = storageKey.slice(ROW_STORAGE_PREFIX.length);
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') {
              _rowCache.set(rck, parsed);
              rowCount++;
            }
          }
        } catch (_e) { /* skip malformed entries */ }
      }
    }
  } catch (_e) {
    _log('hydrate threw', _e && _e.message);
  }
  _log(`hydrate done — ${libCount} libraries + ${rowCount} rows from localStorage`);
}
_hydrateFromStorage();

/** Save/load helpers for the per-row cache. Same best-effort
 *  semantics as full-library persistence. */
function _saveRowToStorage(rck, row) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(ROW_STORAGE_PREFIX + rck, JSON.stringify(row));
  } catch (e) {
    _log(`_saveRowToStorage FAILED ${rck}: ${e && e.name}: ${e && e.message}`);
  }
}

/** Build the cache key. Empty lang is a valid distinct bucket. */
export function cacheKey(key, lang) {
  return `${key}|${lang || ''}`;
}

/**
 * Kick off (or reuse) a fetch of the given library at the given
 * language. Returns a Promise resolving to `{ rows, pk }`. On
 * success or failure, subscribers are notified with the cache key
 * so they can re-render.
 *
 * 0.37.91 — stale-while-revalidate. On the FIRST call per session
 * for a given (key, lang), always fires a fresh network request,
 * even if the in-memory cache is populated from localStorage.
 * That way admin's library edits propagate on next page load
 * without a manual refresh. Subsequent calls in the same session
 * reuse the in-memory result.
 */
export function fetchLibrary(key, lang) {
  const ck = cacheKey(key, lang);
  // 0.37.95 — Check inflight first (parallel refactor to
  // fetchLibraryRow). Dedupes concurrent stale-revalidate callers.
  const inflight = _libraryInflight.get(ck);
  if (inflight) {
    _log(`fetchLibrary REUSING inflight promise ${ck}`);
    return inflight;
  }
  const cached = _cache.get(ck);
  _expireCollection(key, ck);
  if (cached && _refreshedThisSession.has(ck)) {
    _log(`fetchLibrary CACHE HIT (already refreshed) ${ck}`);
    return Promise.resolve(_view(cached));
  }
  const cacheState = cached ? 'populated (stale, refreshing)' : 'empty';
  _log(`fetchLibrary START ${ck} cache=${cacheState}`);
  const fetchStart = typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now();
  const promise = formLibraryRowsApi.list(key, { lang })
    .then(data => {
      const took = Math.round((typeof performance !== 'undefined' && performance.now
        ? performance.now() : Date.now()) - fetchStart);
      // 0.44.72 - As sent; date columns are formatted on read (_view).
      const payload = { rows: data.rows || [], pk: data.pk, column_types: data.column_types || null };
      // 0.44.69 - Month names for the MMM / MMMM date formats.
      if (key === 'months_and_days') _registerMonths(lang, payload.rows);
      _cache.set(ck, payload);
      _libraryInflight.delete(ck);
      _refreshedThisSession.add(ck);
      _refreshedAt.set(ck, Date.now());
      _saveToStorage(ck, payload);
      _log(`fetchLibrary RESOLVED ${ck} rows=${payload.rows.length} took=${took}ms`);
      _listeners.forEach(fn => { _log(`listener notified ${ck}`); fn(ck); });
      return _view(payload);
    })
    .catch(err => {
      _libraryInflight.delete(ck);
      _log(`fetchLibrary FAILED ${ck}: ${err && err.message}`);
      const e = new Error(err && err.message ? err.message : 'Library fetch failed');
      _listeners.forEach(fn => fn(ck));
      throw e;
    });
  _libraryInflight.set(ck, promise);
  return promise;
}


/**
 * 0.37.93 — Point-lookup fetch. Fetches exactly one row from the
 * library where keyColumn == keyValue. Much lighter than
 * `fetchLibrary` for lookup-only usage (labels/hints/etc.):
 * ~200 bytes vs ~2MB response for a 10k-row library.
 *
 * Returns a Promise that resolves to the row dict, or to null
 * on 404 (row-not-found). Rejects only on network errors —
 * "no matching row" is a normal result, not an error.
 *
 * Same stale-while-revalidate semantics as fetchLibrary: first
 * call per session for a given cacheKey always hits the network;
 * subsequent calls reuse the in-memory result.
 */
export function fetchLibraryRow(libKey, keyColumn, keyValue, lang) {
  const rck = _rowCacheKey(libKey, keyColumn, keyValue, lang);
  // 0.37.95 — Check inflight FIRST. Fixes duplicate-fetch bug where
  // N concurrent callers with a stale cached value each fired their
  // own network round-trip.
  const inflight = _rowInflight.get(rck);
  if (inflight) {
    _log(`fetchLibraryRow REUSING inflight promise ${rck}`);
    return inflight;
  }
  const cached = _rowCache.get(rck);
  if (cached !== undefined && _rowRefreshedThisSession.has(rck)) {
    _log(`fetchLibraryRow CACHE HIT (already refreshed) ${rck}`);
    return Promise.resolve(_rowView(cached));
  }
  const cacheState = cached !== undefined ? 'populated (stale, refreshing)' : 'empty';
  _log(`fetchLibraryRow START ${rck} cache=${cacheState}`);
  const fetchStart = typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now();
  const promise = formLibraryRowsApi.row(libKey, { keyColumn, keyValue, lang })
    .then(data => {
      const took = Math.round((typeof performance !== 'undefined' && performance.now
        ? performance.now() : Date.now()) - fetchStart);
      const row = _withTypes((data && data.row) || null, data && data.column_types);
      _rowCache.set(rck, row);
      if (row && row.__types) _typedRowLibs.add(libKey + '|' + (lang || ''));
      _rowInflight.delete(rck);
      _rowRefreshedThisSession.add(rck);
      if (row) _saveRowToStorage(rck, row);
      _log(`fetchLibraryRow RESOLVED ${rck} took=${took}ms hit=${row ? 'yes' : 'no'}`);
      _listeners.forEach(fn => fn(libKey + '|' + (lang || '')));
      return _rowView(row);
    })
    .catch(err => {
      _rowInflight.delete(rck);
      // 404 = row not found — normal result, cache null so we don't
      // re-fetch. Any other error we don't cache; caller can retry.
      if (err && (err.status === 404 || (err.message && /404/.test(err.message)))) {
        _rowCache.set(rck, null);
        _rowRefreshedThisSession.add(rck);
        _saveRowToStorage(rck, null);
        _log(`fetchLibraryRow 404 (row not found) ${rck}`);
        _listeners.forEach(fn => fn(libKey + '|' + (lang || '')));
        return null;
      }
      _log(`fetchLibraryRow FAILED ${rck}: ${err && err.message}`);
      throw err;
    });
  _rowInflight.set(rck, promise);
  return promise;
}


/**
 * 0.37.97 — Batch point-lookup. Resolves N (keyColumn, keyValue,
 * valueColumn) tuples for one library in a single HTTP round-trip.
 *
 * Called by the prewarm useEffect once per (libKey, lang) group —
 * collapses what used to be N individual /row calls into 1
 * /batch-lookup call. Populates _rowCache for each resolved row and
 * fires listeners once at the end.
 *
 * Any tuple already in _rowCache or _rowInflight is skipped from
 * the batch (no wasted work). Empty batches short-circuit and
 * return an immediately-resolved Promise.
 *
 * Returns a Promise that resolves to the array of {keyValue, row}
 * pairs in the order requested. Individual on-demand callers
 * should keep using fetchLibraryRow — this is for the prewarm
 * batch case.
 */
/* ============================================================
 * 0.37.100 — Preload libraries when a page structure arrives.
 *
 * Wires pageCache to call back into libraryCache as soon as
 * pageCache resolves a page (from network fetch). We walk the
 * structure looking for =lib(...) expressions and dispatch
 * batched fetches BEFORE React mounts PageContentRenderer.
 *
 * Effect: overlaps library-load network time with React's
 * initial-render prep time. Was serial (structure → mount →
 * render → useEffect → batch → wait for network); now parallel
 * (structure → batch fires + React mounts concurrently).
 *
 * The callback is registered here at module-load, so any
 * pageCache.fetchPage(...) resolution triggers preload
 * automatically.
 * ============================================================ */

function _preloadLibrariesFromPageStructure(pageData, lang) {
  if (!pageData) return;
  const structure = Array.isArray(pageData.body_json) ? pageData.body_json
    : Array.isArray(pageData.structure) ? pageData.structure
    : Array.isArray(pageData.body) ? pageData.body
    : null;
  if (!structure) return;
  // Walk structure collecting literal lib() calls and library keys —
  // same shape as PageContentRenderer's prewarm useEffect, but
  // isolated here so we can run pre-mount.
  const keys = new Set();
  const literalCalls = [];
  const scanString = (s) => {
    if (typeof s !== 'string' || !s) return;
    for (const k of extractLibKeysFromExpression(s)) keys.add(k);
    for (const call of extractLiteralLibCallsFromExpression(s)) literalCalls.push(call);
  };
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const el of nodes) {
      if (!el) continue;
      scanString(el.label); scanString(el.text); scanString(el.hint);
      scanString(el.help); scanString(el.placeholder); scanString(el.button_label);
      scanString(el.summary_heading); scanString(el.summary_value);
      if (el.type === 'button') scanString(el.action_value);
      if (el.type === 'table' && (!el.source || el.source === 'manual')) {
        if (Array.isArray(el.headers)) for (const h of el.headers) scanString(h);
        if (Array.isArray(el.rows)) for (const row of el.rows) {
          if (Array.isArray(row)) for (const cell of row) scanString(cell);
        }
      }
      if (Array.isArray(el.children)) walk(el.children);
    }
  };
  walk(structure);
  _log(`preloadLibrariesFromPageStructure lang=${lang} keys=${keys.size} literalCalls=${literalCalls.length}`);
  // Group by libKey and fire one batch per library.
  const batched = new Map();
  for (const call of literalCalls) {
    if (!batched.has(call.libKey)) batched.set(call.libKey, []);
    batched.get(call.libKey).push(call);
  }
  for (const [libKey, calls] of batched) {
    fetchLibraryRowsBatch(libKey, calls, lang).catch(() => { /* on-demand path handles */ });
  }
}
setOnStructureResolved(_preloadLibrariesFromPageStructure);

export function fetchLibraryRowsBatch(libKey, lookups, lang) {
  if (!Array.isArray(lookups) || lookups.length === 0) return Promise.resolve([]);
  // Filter out lookups whose value is already cached or in-flight.
  const remaining = [];
  const remainingKeys = [];
  for (const lk of lookups) {
    const rck = _rowCacheKey(libKey, lk.keyColumn, lk.keyValue, lang);
    if (_rowInflight.has(rck)) continue;
    if (_rowCache.has(rck)) continue;
    remaining.push(lk);
    remainingKeys.push(rck);
  }
  if (remaining.length === 0) {
    _log(`fetchLibraryRowsBatch NO-OP (all cached/inflight) libKey=${libKey} lang=${lang}`);
    return Promise.resolve([]);
  }
  _log(`fetchLibraryRowsBatch START libKey=${libKey} lang=${lang} lookups=${remaining.length}`);
  const fetchStart = typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now();
  const promise = formLibraryRowsApi.batchLookup(libKey, {
    lookups: remaining.map(l => ({
      keyColumn: l.keyColumn,
      keyValue: String(l.keyValue),
      valueColumn: l.valueColumn,
    })),
    lang,
  })
    .then(data => {
      const took = Math.round((typeof performance !== 'undefined' && performance.now
        ? performance.now() : Date.now()) - fetchStart);
      const results = (data && Array.isArray(data.results)) ? data.results : [];
      let hits = 0;
      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        const rck = remainingKeys[i];
        const row = r && r.row ? _withTypes(r.row, data && data.column_types) : null;
        if (row && row.__types) _typedRowLibs.add(libKey + '|' + (lang || ''));
        _rowCache.set(rck, row);
        _rowInflight.delete(rck);
        _rowRefreshedThisSession.add(rck);
        if (row) {
          _saveRowToStorage(rck, row);
          hits++;
        }
      }
      _log(`fetchLibraryRowsBatch RESOLVED libKey=${libKey} lang=${lang} took=${took}ms hits=${hits}/${results.length}`);
      _listeners.forEach(fn => fn(libKey + '|' + (lang || '')));
      return results;
    })
    .catch(err => {
      _log(`fetchLibraryRowsBatch FAILED libKey=${libKey} lang=${lang}: ${err && err.message}`);
      // Clear the inflight markers so on-demand callers can retry.
      for (const rck of remainingKeys) _rowInflight.delete(rck);
      throw err;
    });
  // Mark all remaining lookups as in-flight so concurrent single-
  // fetchLibraryRow callers reuse this batched promise.
  for (const rck of remainingKeys) _rowInflight.set(rck, promise);
  return promise;
}

/**
 * Synchronous read. Returns the cached payload (`{ rows, pk }`) if
 * loaded, or null if the library is not-yet-fetched or is currently
 * fetching. Consumers can call this first, and if it returns null,
 * either kick off `fetchLibrary` themselves or leave it to whoever
 * else needs the data.
 */
export function getCachedLibrary(key, lang) {
  const cached = _cache.get(cacheKey(key, lang));
  if (cached && !(cached instanceof Promise)) return _view(cached);
  return null;
}

/**
 * Subscribe to cache-change events. `fn` is called with the changed
 * cacheKey whenever a fetch resolves. Returns an unsubscribe
 * function. Consumers that want "re-render on ANY library load"
 * ignore the argument.
 */
/* 0.44.72 - A new format, page language or set of month names changes
 * how dates read: tell the readers of every library with date columns. */
const _typedRowLibs = new Set();
subscribeFormatChange(() => {
  const keys = new Set(_typedRowLibs);
  for (const [ck, payload] of _cache) {
    if (payload && !(payload instanceof Promise) && payload.column_types
        && Object.keys(payload.column_types).length) keys.add(ck);
  }
  keys.forEach(ck => _listeners.forEach(fn => fn(ck)));
});

export function subscribeLibraryCache(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/**
 * Scan an expression source string and return the set of library
 * keys it references via `lib(...)` calls (0.37.27). Used by the
 * runtime prefetch step so a button or rule that calls
 * lib("foo", ...) has "foo" in cache by the time it fires — no
 * "first click returns 0, second click returns the value" gotcha.
 *
 * Simple regex, NOT a full parse: matches `lib(` followed by a
 * quoted first argument. Coverage is intentionally best-effort —
 * we only prefetch when the library name is a string literal (the
 * common case). Dynamic library names (`lib(fieldRef, ...)`) can't
 * be prefetched anyway since we don't know their value until the
 * expression runs.
 *
 * Returns an array (possibly empty). Duplicates deduped by the
 * consumer's Set. Accepts null / non-string / non-'='-prefixed
 * input safely — returns empty.
 */
export function extractLibKeysFromExpression(str) {
  if (typeof str !== 'string' || str.charAt(0) !== '=') return [];
  const keys = [];
  const re = /\blib\s*\(\s*(["'])([^"']+)\1/g;
  let m;
  while ((m = re.exec(str)) !== null) {
    if (m[2]) keys.push(m[2]);
  }
  return keys;
}

/**
 * 0.37.93 — Extract fully-literal lib() calls from an expression,
 * yielding `{ libKey, keyColumn, keyValue, valueColumn }` tuples
 * that the prewarm useEffect can point-lookup at page-mount time.
 *
 * Only matches calls where ALL FOUR arguments are string or numeric
 * literals (e.g. `lib("ui_strings", "key", "country_label", "value")`
 * or `lib("goods", "key", 10001, "value")`). Calls that reference
 * variables (e.g. `lib("cities", "code", region_code, "name")`)
 * can't be prewarmed at parse time — the keyValue depends on
 * runtime field values — and are skipped. Those still resolve on
 * demand via `lookupLibraryValue`'s tier-3 point-lookup miss path.
 *
 * Regex is intentionally strict — anything more nuanced would need
 * a real parser. False negatives (skipping a prewarmable call)
 * degrade gracefully to the on-demand path; false positives could
 * fire wrong fetches, so we bias to strict.
 */
export function extractLiteralLibCallsFromExpression(str) {
  if (typeof str !== 'string' || str.charAt(0) !== '=') return [];
  const calls = [];
  // lib(  "libKey" ,  "keyColumn" ,  "keyValue"|<digits>  ,  "valueColumn"  )
  // Whitespace around commas allowed; both single and double quotes.
  const re = new RegExp(
    '\\blib\\s*\\(\\s*' +
    '(["\'])([^"\']+)\\1' +          // 1,2 libKey
    '\\s*,\\s*' +
    '(["\'])([^"\']+)\\3' +          // 3,4 keyColumn
    '\\s*,\\s*' +
    '(?:(["\'])([^"\']+)\\5|(\\d+))' +  // 5,6 quoted keyValue OR 7 numeric
    '\\s*,\\s*' +
    '(["\'])([^"\']+)\\8' +          // 8,9 valueColumn
    '\\s*\\)',
    'g',
  );
  let m;
  while ((m = re.exec(str)) !== null) {
    calls.push({
      libKey:      m[2],
      keyColumn:   m[4],
      keyValue:    m[6] !== undefined ? m[6] : m[7],
      valueColumn: m[9],
    });
  }
  return calls;
}

/**
 * 0.37.57 — Pull field names referenced by `labels_of(<name>)`
 * calls out of an expression string. Companion to
 * extractLibKeysFromExpression above — but where lib("key", ...)
 * carries the library name inline, labels_of takes a FIELD
 * name; the caller has to look that field's element up in the
 * form structure to find its `library` key, then prefetch.
 *
 * Accepts both forms admin can write:
 *
 *   labels_of("field_name")   → captures field_name
 *   labels_of(field_name)     → captures field_name (bare identifier)
 *
 * Skips whitespace tolerantly. Duplicates deduped by the caller.
 *
 * Prefetch matters here specifically because the "first click on
 * a button that fires set_value = =join(labels_of(x), ",") shows
 * IDs, second click shows labels" bug traced to labels_of()
 * hitting a cold cache: fetch fires async, the rule already
 * wrote raw values to the target, and the rule doesn't re-fire
 * (edge-triggered click) after the fetch settles. Prewarming
 * the library at page load closes that race.
 */
export function extractLabelsOfFieldsFromExpression(str) {
  if (typeof str !== 'string' || str.charAt(0) !== '=') return [];
  const fields = [];
  // Quoted form: labels_of("name") or labels_of('name')
  const reQuoted = /\blabels_of\s*\(\s*(["'])([^"']+)\1/g;
  let m;
  while ((m = reQuoted.exec(str)) !== null) {
    if (m[2]) fields.push(m[2]);
  }
  // Bare form: labels_of(identifier) — identifier chars only
  const reBare = /\blabels_of\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)/g;
  while ((m = reBare.exec(str)) !== null) {
    if (m[1]) fields.push(m[1]);
  }
  return fields;
}

/**
 * Look up ONE cell value in a library — the runtime form-rule
 * `lib()` function calls this.
 *
 *   lookupLibraryValue(libKey, keyColumn, keyValue, valueColumn, lang)
 *     → the value of `valueColumn` on the row where `keyColumn`
 *       equals `keyValue`
 *     → undefined if the library isn't cached, the key column
 *       doesn't match any row, or the requested value column
 *       doesn't exist on that row
 *
 * If the library isn't cached yet, ALSO kicks off a fetch so the
 * next render (after the fetch settles) can succeed. Subscribers
 * (subscribeLibraryCache) get notified when the fetch completes.
 *
 * Comparison is string-based (both sides String()'d) so a citizen
 * typing "12" matches a library row with numeric 12 without admin
 * having to think about types.
 *
 * Translatable value columns come back as `{ en, hy, ru, ... }`
 * objects from the API. We resolve to the requested language when
 * possible; missing translations fall through to English, then to
 * whichever key sorts first — and if the object is empty, undefined.
 */
export function lookupLibraryValue(libKey, keyColumn, keyValue, valueColumn, lang) {
  if (!libKey || !keyColumn || !valueColumn) return undefined;
  // 0.46.19 - A DEFINITE miss is "" and only "still loading" is
  // undefined. undefined keeps a whole formula undefined (it is
  // waiting), so `if(lib(...) = "", fallback, lib(...))` showed
  // nothing at all for a code the library does not have, instead of
  // its fallback. No key can match no row, so an empty key is a miss.
  if (keyValue === undefined || keyValue === null || keyValue === '') return '';

  // 0.38.50 - Background revalidation on cache hit.
  //
  // The stated design (0.37.91 comment above) is stale-while-
  // revalidate: return cached data synchronously, fire a fresh
  // network fetch in the background, notify listeners when the
  // fresh data arrives so the UI re-renders. That contract lives
  // in fetchLibrary / fetchLibraryRow (they check
  // _refreshedThisSession and only fetch once per session).
  //
  // But lookupLibraryValue was reading directly from _cache and
  // _rowCache and NEVER calling fetchLibrary/fetchLibraryRow on
  // a cache hit - so the revalidation only fired when there was
  // a MISS. Consequence: a library value hydrated from
  // localStorage on module load kept serving forever, and admin
  // edits to library rows never propagated for existing browsers
  // (which had the stale row cached from a prior session).
  // Incognito and fresh browsers worked because their localStorage
  // was empty - the tier-3 miss path fired the fetch.
  //
  // Fix: whenever we return a cached value from tier 1 or tier 2
  // AND this session hasn't refreshed the underlying source yet,
  // kick off a background fetch. The current call returns the
  // cached (possibly stale) value immediately; when the fresh
  // fetch resolves, the row cache is updated and listeners
  // notify subscribers to re-render.
  const ck = cacheKey(libKey, lang);
  const rck = _rowCacheKey(libKey, keyColumn, keyValue, lang);

  // Tier 1: full library cached? Use it (works for pages that
  // also render a dropdown/multiselect backed by the same library).
  const cached = getCachedLibrary(libKey, lang);
  if (cached) {
    const target = String(keyValue);
    // 0.45.49 - In a content library ('collection:<key>') business_id is
    // the item's id: its rows carry it as `id`.
    const kc = (keyColumn === 'business_id' && String(libKey).startsWith('collection:')) ? 'id' : keyColumn;
    let row = cached.rows.find(r => r && (String(r[kc]) === target || (r[`${kc}_iso`] !== undefined && String(r[`${kc}_iso`]) === target)));
    // 0.46.35 - No exact match: the key again without regard to letter
    // case ("BE" finds "be"), the first such row in the library's order.
    // The server's /row and batch lookups do the same.
    if (!row && target.toLowerCase() !== target.toUpperCase()) {
      const want = target.trim().toLowerCase();
      row = cached.rows.find(r => r && r[kc] != null && typeof r[kc] !== 'object'
        && String(r[kc]).toLowerCase() === want);
    }
    if (!row) {
      _log(`lookupLibraryValue NO-ROW (full-lib cache) ${libKey}|${lang} key=${keyValue}`);
      // 0.38.50 - Even on NO-ROW, if the full library hasn't been
      // refreshed this session, revalidate: maybe admin ADDED the
      // row since our cached snapshot.
      if (!_refreshedThisSession.has(ck) && !_libraryInflight.has(ck)) {
        _log(`lookupLibraryValue background-refresh (was NO-ROW) ${ck}`);
        fetchLibrary(libKey, lang).catch(() => { /* listeners handle failure */ });
      }
      return '';
    }
    // 0.38.50 - Fire background revalidation of the full library
    // if we haven't already this session. Fetch dedupes via
    // _libraryInflight and _refreshedThisSession, so calling it
    // here is safe (one network hit per session, not per lookup).
    if (!_refreshedThisSession.has(ck) && !_libraryInflight.has(ck)) {
      _log(`lookupLibraryValue background-refresh (tier-1 hit) ${ck}`);
      fetchLibrary(libKey, lang).catch(() => { /* listeners handle failure */ });
    }
    return _extractLangValue(row, valueColumn, lang, `full-lib ${libKey}|${lang} key=${keyValue}`);
  }

  // Tier 2: per-row cache. If we've fetched this exact row before,
  // use it — no network round-trip. 0.37.93.
  const rowCached = _rowCache.get(rck);
  if (rowCached !== undefined && !(rowCached instanceof Promise)) {
    // 0.38.50 - Fire background revalidation of just this row on
    // first use per session. fetchLibraryRow dedupes via
    // _rowInflight and _rowRefreshedThisSession.
    if (!_rowRefreshedThisSession.has(rck) && !_rowInflight.has(rck)) {
      _log(`lookupLibraryValue background-refresh (tier-2 hit) ${rck}`);
      fetchLibraryRow(libKey, keyColumn, keyValue, lang).catch(() => { /* listeners handle failure */ });
    }
    if (rowCached === null) {
      _log(`lookupLibraryValue NO-ROW (row cache) ${libKey}|${lang} key=${keyValue}`);
      return '';
    }
    return _extractLangValue(_rowView(rowCached), valueColumn, lang, `row-cache ${libKey}|${lang} key=${keyValue}`);
  }

  // Tier 3: miss. Fire the point-lookup (not the full library fetch)
  // and return undefined for now. When it resolves, listeners notify
  // and the caller re-renders. 0.37.93 — was firing full library
  // fetch here; now uses fetchLibraryRow for a ~50ms round-trip
  // instead of a ~3000ms one.
  _log(`lookupLibraryValue MISS ${libKey}|${lang} key=${keyValue} → point-lookup`);
  fetchLibraryRow(libKey, keyColumn, keyValue, lang).catch(() => {
    /* subscribers already notified via .catch inside fetchLibraryRow */
  });
  return undefined;
}

/** 0.37.93 — Extracted from the previous lookupLibraryValue body:
 *  given a row and a value column + lang, return the language-
 *  resolved value with fall-throughs (requested lang → en → any).
 *  Kept as a helper so both the full-library and per-row cache
 *  paths share one implementation. */
function _extractLangValue(row, valueColumn, lang, ctxLabel) {
  const raw = row[valueColumn];
  if (raw === undefined || raw === null) {
    _log(`lookupLibraryValue NULL-VALUE (${ctxLabel}) col=${valueColumn}`);
    return '';
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    if (lang && raw[lang] !== undefined && raw[lang] !== '') {
      _log(`lookupLibraryValue HIT (${ctxLabel}) → "${raw[lang]}"`);
      return raw[lang];
    }
    if (raw.en !== undefined && raw.en !== '') {
      _log(`lookupLibraryValue HIT-EN-FALLBACK (${ctxLabel}) → "${raw.en}"`);
      return raw.en;
    }
    const firstKey = Object.keys(raw)[0];
    if (firstKey) {
      _log(`lookupLibraryValue HIT-ANY-LANG (${ctxLabel}) → "${raw[firstKey]}"`);
      return raw[firstKey];
    }
    _log(`lookupLibraryValue EMPTY-OBJECT (${ctxLabel})`);
    return '';
  }
  _log(`lookupLibraryValue HIT (${ctxLabel}) → "${raw}"`);
  return raw;
}
